import { useCallback, useEffect, useMemo, useState } from "react";
import { reportsService } from "@/services";
import type { EarningsReport as EarningsData } from "@/services/types";
import { PageHeader, BreadcrumbItem } from "@/components/feature/PageHeader";
import { Card } from "@/components/base/Card";
import { Badge } from "@/components/base/Badge";
import { Button } from "@/components/base/Button";
import { EmptyState } from "@/components/base/EmptyState";
import { ErrorState } from "@/components/base/ErrorState";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { formatINR } from "@/utils/format";
import { exportReportCSV, csvAmount } from "@/utils/report";

export default function EarningsReportPage() {
  const [data, setData] = useState<EarningsData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await reportsService.getEarningsReport());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load the earnings report.");
      setData(null);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const byPeriod = useMemo(() => {
    if (!data) return [];
    const map = new Map<number, { year: number; reserved: number; commission: number; total: number }>();
    for (const g of data.groups) {
      const year = Number(g.groupCode.slice(0, 4)) || new Date().getFullYear();
      const e = map.get(year) ?? { year, reserved: 0, commission: 0, total: 0 };
      e.reserved += g.reservedPool;
      e.commission += g.commission;
      e.total += g.totalEarning;
      map.set(year, e);
    }
    return Array.from(map.values()).sort((a, b) => a.year - b.year);
  }, [data]);

  const handleExport = () => {
    if (!data) return;
    exportReportCSV({
      filename: "organizer-earnings",
      reportName: "Organizer Earnings Report",
      filters: ["All groups"],
      columns: [
        { header: "Group", value: (r) => r.group },
        { header: "Reserved cycle", value: (r) => r.cycle },
        { header: "Passed", value: (r) => r.passed },
        { header: "Reserved pool", value: (r) => csvAmount(r.reserved) },
        { header: "Commission", value: (r) => csvAmount(r.commission) },
        { header: "Total earning", value: (r) => csvAmount(r.total) },
      ],
      rows: data.groups.map((g) => ({
        group: `${g.groupName} (${g.groupCode})`,
        cycle: g.reservedCycle,
        passed: g.reservedCyclePassed ? "Yes" : "No",
        reserved: g.reservedPool,
        commission: g.commission,
        total: g.totalEarning,
      })),
    });
  };

  return (
    <div>
      <PageHeader
        breadcrumb={
          <>
            <BreadcrumbItem label="Home" to="/" />
            <BreadcrumbItem label="Reports" to="/reports" />
            <BreadcrumbItem label="Organizer earnings" isLast />
          </>
        }
        title="Organizer earnings"
        subtitle="What the organizer has actually earned — the reserved full-pool cycles plus any commission."
        actions={
          <Button variant="outline" size="sm" icon={<i className="ri-download-2-line" />} onClick={handleExport}>
            Export
          </Button>
        }
      />

      {error ? <div className="mb-3"><ErrorState message={error} onRetry={load} /></div> : null}

      {!data ? (
        <div className="h-64 animate-pulse rounded-lg bg-background-200" />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Card padding={false}>
              <div className="p-4">
                <p className="text-xs text-foreground-500">Total reserved pool earned</p>
                <p className="tnum mt-1.5 text-2xl font-semibold text-foreground-950">{formatINR(data.totalReserved)}</p>
              </div>
            </Card>
            <Card padding={false}>
              <div className="p-4">
                <p className="text-xs text-foreground-500">Commission earned</p>
                <p className="tnum mt-1.5 text-2xl font-semibold text-foreground-950">{formatINR(data.totalCommission)}</p>
              </div>
            </Card>
            <Card padding={false}>
              <div className="p-4">
                <p className="text-xs text-foreground-500">Total earnings</p>
                <p className="tnum mt-1.5 text-2xl font-semibold text-accent-700">{formatINR(data.totalEarning)}</p>
              </div>
            </Card>
          </div>

          <Card className="mt-3">
            <div className="overflow-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-background-100/80 text-xs font-semibold uppercase tracking-wide text-foreground-500">
                    <th className="px-3 py-2 text-left">Group</th>
                    <th className="px-3 py-2 text-right">Reserved cycle</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-right">Reserved pool</th>
                    <th className="px-3 py-2 text-right">Commission</th>
                    <th className="px-3 py-2 text-right">Total earning</th>
                  </tr>
                </thead>
                <tbody>
                  {data.groups.map((g) => (
                    <tr key={g.groupId} className="border-t border-background-100 hover:bg-background-100/50">
                      <td className="px-3 py-2 font-medium text-foreground-900">{g.groupName}<span className="tnum ml-1 text-xs text-foreground-400">{g.groupCode}</span></td>
                      <td className="tnum px-3 py-2 text-right">{g.reservedCycle}</td>
                      <td className="px-3 py-2">
                        {g.reservedCyclePassed ? <Badge color="success">Taken</Badge> : <Badge color="warning">Pending</Badge>}
                      </td>
                      <td className="px-3 py-2 text-right"><CurrencyDisplay amount={g.reservedPool} hideSymbol /></td>
                      <td className="px-3 py-2 text-right text-foreground-700"><CurrencyDisplay amount={g.commission} hideSymbol /></td>
                      <td className="px-3 py-2 text-right"><CurrencyDisplay amount={g.totalEarning} hideSymbol className="font-medium" /></td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-background-200 bg-background-100/60 font-semibold">
                    <td className="px-3 py-2 text-foreground-500" colSpan={3}>Total</td>
                    <td className="px-3 py-2 text-right"><CurrencyDisplay amount={data.totalReserved} hideSymbol /></td>
                    <td className="px-3 py-2 text-right text-foreground-700"><CurrencyDisplay amount={data.totalCommission} hideSymbol /></td>
                    <td className="px-3 py-2 text-right"><CurrencyDisplay amount={data.totalEarning} hideSymbol /></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>

          {byPeriod.length > 1 ? (
            <Card className="mt-3" title="Summary by period" subtitle="Earnings grouped by the year each group began.">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {byPeriod.map((p) => (
                  <div key={p.year} className="rounded-md border border-background-200 bg-background-100/50 p-3">
                    <p className="text-xs font-medium text-foreground-500">Year {p.year}</p>
                    <p className="tnum mt-1 text-lg font-semibold text-foreground-950">{formatINR(p.total)}</p>
                    <p className="mt-1 text-xs text-foreground-500">
                      <span className="tnum">{formatINR(p.reserved)}</span> reserved · <span className="tnum">{formatINR(p.commission)}</span> commission
                    </p>
                  </div>
                ))}
              </div>
            </Card>
          ) : null}

          {data.groups.length === 0 ? (
            <Card className="mt-3">
              <EmptyState icon="ri-money-rupee-circle-line" title="No earnings yet" description="No reserved organizer cycles have been taken." />
            </Card>
          ) : null}
        </>
      )}
    </div>
  );
}